import React, { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Login from "pages/Login";
import AuthCallback from "pages/AuthCallback";
import PlaidLink from "components/PlaidLink";
import { useAuth } from "hooks/useAuth";
import { loginSuccess } from "store/slices/authSlice";
import { getApiUrl } from "utils/config";
import { PageLoading } from "design/ui";
import { logError } from "utils/errorHandler";
import { plaidService, PlaidDuplicateItemError } from "services/plaidService";
import { StatementProvider } from "design/StatementContext";
import { LinkedDialog, LinkProgress } from "design/components/LinkedDialog";

const queryClient = new QueryClient();

/** Plaid's word for "every transaction it will ever backfill has been served". */
const HISTORY_COMPLETE = "HISTORICAL_UPDATE_COMPLETE";
/** Right after Link, Plaid is usually still pulling history, so the first sync
 *  pass comes back short or empty. Retry for about a minute, then leave the
 *  rest to the daily sync job. */
const SYNC_ATTEMPTS = 8;
const SYNC_INTERVAL_MS = 8000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const Landing = lazy(() => import("pages/Landing"));
const StatementLayout = lazy(() => import("design/components/StatementLayout"));
const StatementOverview = lazy(() => import("design/pages/Overview"));
const StatementAi = lazy(() => import("design/pages/Ai"));
const StatementSpending = lazy(() => import("design/pages/Spending"));
const StatementTransactions = lazy(() => import("design/pages/Transactions"));
const StatementAccounts = lazy(() => import("design/pages/Accounts"));
const StatementWealth = lazy(() => import("design/pages/Wealth"));
const StatementRules = lazy(() => import("design/pages/Rules"));
const StatementSettings = lazy(() => import("design/pages/Settings"));
const StatementPockets = lazy(() => import("design/pages/Pockets"));
const StatementFeedback = lazy(() => import("design/pages/Feedback"));
const StatementJoinPocket = lazy(() => import("design/pages/JoinPocket"));
// Every token and component on one page; see DESIGN.md. Not shipped to production.
const DesignGallery = lazy(() => import("design/pages/DesignGallery"));

/** Same key as `design/pockets/model`, kept literal so the shell need not load it. */
const PENDING_JOIN_KEY = "aif.pendingJoin";

type UUID = string;

function tokenPayloadSub(token: string): string | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(atob(payload)) as { sub?: string };
    return decoded.sub ?? null;
  } catch {
    return null;
  }
}

const SuspenseFallback = () => <PageLoading />;

const App: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const dispatch = useDispatch();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const isAuthCallback = location.pathname.endsWith("/auth/callback");

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [showPlaidLink, setShowPlaidLink] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkProgress, setLinkProgress] = useState<LinkProgress | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const controller = new AbortController();
      let timeoutId: NodeJS.Timeout | null = setTimeout(() => controller.abort(), 10000);
      const validateToken = async () => {
        try {
          const response = await fetch(`${getApiUrl()}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          });
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          if (response.ok) {
            const userData = await response.json();
            dispatch(
              loginSuccess({
                id: userData.user_id as UUID,
                email: userData.email ?? "user@example.com",
                name: userData.name ?? "",
              }),
            );
          } else if (response.status === 401) {
            localStorage.removeItem("token");
          } else {
            const fallbackId = tokenPayloadSub(token) ?? "user";
            dispatch(loginSuccess({ id: fallbackId, email: "", name: "" }));
          }
        } catch (error: unknown) {
          if (timeoutId) clearTimeout(timeoutId);
          if (error instanceof Error && error.name !== "AbortError") logError(error, "App.tokenValidation");
          const fallbackId = tokenPayloadSub(token) ?? "user";
          dispatch(loginSuccess({ id: fallbackId, email: "", name: "" }));
        } finally {
          setIsValidating(false);
        }
      };
      validateToken();
    } else {
      setIsValidating(false);
    }
  }, [dispatch]);

  const startPlaidLink = useCallback(async () => {
    if (linking) return;
    try {
      setLinking(true);
      const data = await plaidService.createLinkToken();
      setLinkToken(data.link_token);
      setShowPlaidLink(true);
    } catch {
      setLinking(false);
    }
  }, [linking]);

  // Updates only the dialog for this item, so a sync still running for the
  // previous bank cannot overwrite the one the user just linked.
  const updateProgress = useCallback((itemId: string, patch: Partial<LinkProgress>) => {
    setLinkProgress((p) => (p && p.itemId === itemId ? { ...p, ...patch } : p));
  }, []);

  // complete-link only syncs accounts and investments; transactions follow here.
  // Every pass refetches every query, so new rows appear without a reload.
  const runInitialSync = useCallback(
    async (itemId: string) => {
      queryClient.invalidateQueries();
      updateProgress(itemId, { phase: "syncing" });
      let added = 0;
      try {
        for (let attempt = 0; attempt < SYNC_ATTEMPTS; attempt++) {
          if (attempt > 0) await sleep(SYNC_INTERVAL_MS);
          const result = await plaidService.syncItemTransactions(itemId, 300_000);
          added += result.added ?? 0;
          await queryClient.invalidateQueries();
          updateProgress(itemId, { transactions: added });
          // An API without update_status cannot say more is coming; one pass is all it gets.
          if (!result.update_status || result.update_status === HISTORY_COMPLETE) break;
        }
        updateProgress(itemId, { phase: "done" });
      } catch (e) {
        logError(e, "App.runInitialSync");
        // The accounts are saved; a failed transaction pull is caught up by the daily sync.
        updateProgress(itemId, { phase: "done" });
      }
    },
    [updateProgress],
  );

  const handlePlaidSuccess = useCallback(
    async (
      publicToken: string,
      metadata: { institution?: { institution_id?: string; name?: string } | null; accounts?: unknown[] },
    ) => {
      const institution = metadata?.institution?.name ?? "Your bank";
      const accounts = metadata?.accounts?.length ?? 0;
      const linked = async (force: boolean) => {
        setLinkProgress({ itemId: null, institution, accounts, transactions: 0, phase: "connecting" });
        const { item_id } = await plaidService.completeLink(publicToken, force);
        setLinkProgress((p) => (p && p.itemId === null ? { ...p, itemId: item_id } : p));
        // Linking another bank is allowed while this one's transactions are still syncing.
        setLinking(false);
        void runInitialSync(item_id);
      };
      try {
        // Client-side duplicate check (fast path). Backend re-validates and returns 409 if we race a concurrent link.
        const institutionId = metadata?.institution?.institution_id;
        let force = false;
        if (institutionId) {
          try {
            const items = await plaidService.getUserItems();
            const dup = (Array.isArray(items) ? items : []).find(
              (i: { institution_id?: string; is_active?: boolean }) =>
                i.institution_id === institutionId && i.is_active !== false,
            );
            if (dup) {
              const label = metadata?.institution?.name ?? "this bank";
              const ok = window.confirm(
                `${label} is already linked.\n\nReplace it? This will:\n  • delete the existing connection's transaction history\n  • spend ONE of your 10 Plaid Trial Item slots — permanently\n\nCancel and use Reconnect from the Accounts page if the existing connection just needs re-authentication (Reconnect is free).`,
              );
              if (!ok) return;
              force = true;
            }
          } catch (e) {
            logError(e, "App.handlePlaidSuccess.dedupeCheck");
            // If we can't verify, still call completeLink — the backend will 409 on duplicate.
          }
        }

        try {
          await linked(force);
        } catch (e) {
          if (e instanceof PlaidDuplicateItemError) {
            const ok = window.confirm(
              `${e.institutionName} is already linked.\n\nReplace it? This deletes the existing connection's transaction history AND spends one of your 10 Plaid Trial Item slots. Use Reconnect from the Accounts page instead if the existing connection just needs re-authentication.`,
            );
            if (!ok) {
              setLinkProgress(null);
              return;
            }
            await linked(true);
          } else {
            throw e;
          }
        }
      } catch (e) {
        logError(e, "App.handlePlaidSuccess");
        setLinkProgress((p) =>
          p && p.itemId === null ? { ...p, phase: "error", error: e instanceof Error ? e.message : undefined } : p,
        );
      } finally {
        setShowPlaidLink(false);
        setLinkToken(null);
        setLinking(false);
      }
    },
    [runInitialSync],
  );

  const handlePlaidExit = useCallback(() => {
    setShowPlaidLink(false);
    setLinkToken(null);
    setLinking(false);
  }, []);

  // An invite link opened while signed out is remembered across the sign-in,
  // then resumed: the landing page has no idea what a pocket is.
  useEffect(() => {
    if (isValidating) return;
    const m = location.pathname.match(/^\/join\/([^/]+)/);
    try {
      if (m && !isAuthenticated) window.localStorage.setItem(PENDING_JOIN_KEY, m[1]);
    } catch {
      /* ignore */
    }
  }, [isValidating, isAuthenticated, location.pathname]);

  const pendingJoin = (() => {
    try {
      return isAuthenticated ? window.localStorage.getItem(PENDING_JOIN_KEY) : null;
    } catch {
      return null;
    }
  })();

  if (isValidating) {
    return <PageLoading />;
  }

  if (pendingJoin && !location.pathname.startsWith("/join/") && !isAuthCallback) {
    return <Navigate to={`/join/${pendingJoin}`} replace />;
  }

  const routes = (
    <>
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" replace />} />

          {isAuthenticated && (
            <Route
              element={
                <Suspense fallback={<SuspenseFallback />}>
                  <StatementLayout onConnectAccount={startPlaidLink} />
                </Suspense>
              }
            >
              <Route path="/" element={<Suspense fallback={<SuspenseFallback />}><StatementOverview /></Suspense>} />
              <Route path="/spending" element={<Suspense fallback={<SuspenseFallback />}><StatementSpending /></Suspense>} />
              <Route path="/transactions" element={<Suspense fallback={<SuspenseFallback />}><StatementTransactions /></Suspense>} />
              <Route path="/accounts" element={<Suspense fallback={<SuspenseFallback />}><StatementAccounts /></Suspense>} />
              <Route path="/rules" element={<Suspense fallback={<SuspenseFallback />}><StatementRules /></Suspense>} />
              <Route path="/pockets" element={<Suspense fallback={<SuspenseFallback />}><StatementPockets /></Suspense>} />
              <Route path="/join/:token" element={<Suspense fallback={<SuspenseFallback />}><StatementJoinPocket /></Suspense>} />
              <Route path="/investments" element={<Suspense fallback={<SuspenseFallback />}><StatementWealth /></Suspense>} />
              <Route path="/settings" element={<Suspense fallback={<SuspenseFallback />}><StatementSettings /></Suspense>} />
              <Route path="/ai" element={<Suspense fallback={<SuspenseFallback />}><StatementAi /></Suspense>} />
              <Route path="/ai/:requestId" element={<Suspense fallback={<SuspenseFallback />}><StatementAi /></Suspense>} />
              <Route path="/feedback" element={<Suspense fallback={<SuspenseFallback />}><StatementFeedback /></Suspense>} />
              <Route path="/feedback/:feedbackId" element={<Suspense fallback={<SuspenseFallback />}><StatementFeedback /></Suspense>} />
              {/* Guides became AI; old links and bookmarks land there. */}
              <Route path="/guides/*" element={<Navigate to="/ai" replace />} />
              {process.env.NODE_ENV !== "production" && (
                <Route path="/design" element={<Suspense fallback={<SuspenseFallback />}><DesignGallery /></Suspense>} />
              )}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          )}

          {/* Unauthenticated visitors get the public landing page at "/" and are
              sent there from anywhere else. Authenticated "/" is unchanged, so
              a signed-in user never sees it. */}
          {!isAuthenticated && !isAuthCallback && (
            <>
              <Route path="/" element={<Suspense fallback={<SuspenseFallback />}><Landing /></Suspense>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>

        {showPlaidLink && linkToken && (
          <PlaidLink linkToken={linkToken} onSuccess={handlePlaidSuccess} onExit={handlePlaidExit} />
        )}

        {linkProgress && (
          <LinkedDialog
            progress={linkProgress}
            onClose={() => setLinkProgress(null)}
            onLinkAnother={() => {
              setLinkProgress(null);
              void startPlaidLink();
            }}
          />
        )}
    </>
  );

  return (
    <QueryClientProvider client={queryClient}>
      <StatementProvider>{routes}</StatementProvider>
    </QueryClientProvider>
  );
};

export default App;
